module Client.Admin.ManageNews.Edit

open System
open Shared
open Client
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Admin.ManageNews
open Shared.Admin.ManageNews.Edit

let [<Literal>] CONTENT_INPUT_ID = "newsContentInput"

type Model = 
    {
        NewsArticleId: NewsArticleId option
        Content: string option
        ContentErrors: string option
        Headline: Text.Model
        StartShowing: DatePicker.Model
        StopShowing: DatePicker.Model
        Author: Text.Model
        Loading: bool
        Saving: bool
    }

module Validations = 
    open Illy.SafeStack.Validation
    open Shared.Admin.ManageNews.Edit.Validations

    let resetValidations (model: Model) = 
        {model with 
            Headline = Text.resetValidations model.Headline
            StartShowing = DatePicker.resetValidations model.StartShowing
            StopShowing = DatePicker.resetValidations model.StopShowing
            ContentErrors =  None
            Author = Text.resetValidations model.Author
        }

    let dispatchValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = 
                err 
                |> ValidationInfrastructure.bundleValidations 
                    (fun x -> 
                        (match x with 
                        | HeadlineValidations _ -> 0
                        | StartShowingValidations _ -> 1
                        | ContentValidations _ -> 2
                        | AuthorValidations _ -> 3), NewsArticleValidations.toString x)
                    4
            {model with 
                Headline = Text.setValidations model.Headline errors.[0] None
                StartShowing = DatePicker.setValidations model.StartShowing errors.[1] None
                ContentErrors = errors.[2]
                Author = Text.setValidations model.Author errors.[3] None
            }

type Msg = 
    | ApiError of ApiError
    | EditDataLoaded of DTO
    | NewDataLoaded of string
    | SaveResult of FailureReason option
    | ContentMsg of string
    | HeadlineMsg of Text.Msg
    | StartShowingMsg of DatePicker.Msg
    | StopShowingMsg of DatePicker.Msg
    | AuthorMsg of Text.Msg
    | CloseOverlay
    | SaveClicked
    | UploadError of string
    | EndNotification

type Event = 
    | CloseOverlay 
    | SaveSuccessful of notification: string
    | AppError of AppError 

module Calls =
    let getEditData nId =
        Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getEditData) nId EditDataLoaded Msg.ApiError

    let getNewData() =
        Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getNewData) () NewDataLoaded Msg.ApiError

module Initialisations =
    let initHeadline value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "Headline" "Headline" (Some "Enter Headline") 100. true value

    let initStartShowing value =
        DatePicker.init "StartShowing" "Start Showing" (Some "Enter Start Showing") true true value

    let initStopShowing value =
        DatePicker.init "StopShowing" "Stop Showing" (Some "Enter Stop Showing") false true value

    let initAuthor value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "Author" "Author" (Some "Enter Author") 100. false value

let init newsArticleId = 
    {
        NewsArticleId = newsArticleId
        Content = None
        ContentErrors = None
        Headline = Initialisations.initHeadline None
        StartShowing = Initialisations.initStartShowing None
        StopShowing = Initialisations.initStopShowing None
        Author = Initialisations.initAuthor None
        Loading = Option.isSome newsArticleId
        Saving = false
    },
    match newsArticleId with
    | Some nId -> Calls.getEditData nId
    | None -> Calls.getNewData()

let toDTO (model: Model): DTO = 
    {
        Headline = Text.toString model.Headline
        StartShowing = DatePicker.toOption model.StartShowing
        StopShowing = DatePicker.toOption model.StopShowing
        Content = model.Content
        Author = Text.toString model.Author
    }

let update model msg = 
    match msg with 
    | EditDataLoaded d -> 
        {model with 
            Headline =  Initialisations.initHeadline d.Headline
            StartShowing = Initialisations.initStartShowing d.StartShowing
            StopShowing = Initialisations.initStopShowing d.StopShowing
            Content = d.Content
            Author = Initialisations.initAuthor d.Author
            Loading = false
        }, SideEffect.none
    | NewDataLoaded author ->
        {
            model with
                Author = Initialisations.initAuthor (Some author)
                Loading = false
        }, SideEffect.none
    | Msg.ApiError err -> {model with Saving = false}, err |> AppError.ApiError |> AppError |> SideEffect.Event 
    | HeadlineMsg cMsg ->
        let m, c = Text.update cMsg model.Headline
        {model with Headline = m}, Cmd.map HeadlineMsg c |> SideEffect.Cmd
    | StartShowingMsg cMsg ->
        let m, c = DatePicker.update model.StartShowing cMsg
        {model with StartShowing = m}, Cmd.map StartShowingMsg c |> SideEffect.Cmd
    | StopShowingMsg cMsg ->
        let m, c = DatePicker.update model.StopShowing cMsg
        {model with StopShowing = m}, Cmd.map StopShowingMsg c |> SideEffect.Cmd
    | AuthorMsg cMsg ->
        let m, c = Text.update cMsg model.Author
        {model with Author = m}, Cmd.map AuthorMsg c |> SideEffect.Cmd
    | ContentMsg str -> {model with Content = Some str}, SideEffect.none
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | SaveClicked -> 
        let dto = toDTO model 
        let vResult = Validations.validateDTO dto
        if Validation.isSuccessful false vResult then 
            {model with Saving = true}, 
            model.NewsArticleId 
            |> Option.map
                (fun nId -> Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.updateArticle nId) dto SaveResult Msg.ApiError)
            |> Option.defaultValue
                (Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.createArticle) dto SaveResult Msg.ApiError)
            |> SideEffect.Cmd
        else 
            Validations.dispatchValidations model vResult, SideEffect.none
    | SaveResult reason -> 
        match reason with 
        | None
        | Some NothingToDo -> 
            model, 
            model.NewsArticleId 
            |> Option.map (fun _ -> "updated") 
            |> Option.defaultValue "created" 
            |> sprintf "News Article was %s successfully." 
            |> Event.SaveSuccessful 
            |> SideEffect.Event
        | Some ItemWasDeleted -> model, "News Article" |> AppError.ItemWasDeleted |> Event.AppError |> SideEffect.Event
        | Some (FailedValidations v) ->
            Validations.dispatchValidations {model with Saving = false} v, SideEffect.none
    | UploadError error -> model, SafeStack.Client.Notification.error error EndNotification |> SideEffect.Cmd
    | EndNotification -> model, SideEffect.none

let view model dispatch = 
    let title = model.NewsArticleId |> Option.map (fun _ -> "Edit News Article") |> Option.defaultValue "Create News Article"
    jQueryUI.modal title 50 Msg.CloseOverlay dispatch (Some 800) [
        yield Text.text model.Loading model.Headline (HeadlineMsg >> dispatch)
        yield div [
            ClassName "fields-inline input-group"
        ] [
            DatePicker.date model.StartShowing (StartShowingMsg >> dispatch) model.Loading []
            DatePicker.date model.StopShowing (StopShowingMsg >> dispatch) model.Loading []
        ]
        if not model.Loading then
            yield Summernote.richText {
                Id = CONTENT_INPUT_ID
                Template = model.Content
                OnChange = ContentMsg >> dispatch
                OnUploadFile = Summernote.defaultImageUploadHandler
                   ""
                   (fun () -> None)
                   (fun _ -> "LinksManager does not currently support image uploads" |> UploadError |> dispatch)
                Props = Summernote.SummernoteParams.defaults
                Host =
                    jQueryUI.textareaWithMandatory
                        CONTENT_INPUT_ID
                        "Content"
                        model.ContentErrors 
                        None 
                        (Some (sprintf "Type in a News Article."))
                        ContentMsg
                        dispatch 
                        4000. 
                        model.Content
                        model.Loading
                        true

            }
        yield Text.text model.Loading model.Author (AuthorMsg >> dispatch)

        yield! UI.saveControls None model.Loading model.Saving SaveClicked Msg.CloseOverlay dispatch
    ]